import type React from "react"
import { useState, useEffect } from "react"
import closeIcon from "../assets/ic_alert_close.svg"
import { Box, Typography, useTheme } from "@mui/material"
import { CloseButton, StyledModal } from "./TaskDetailEdit/TaskDetailsDropdownModal"
import CommonButton from "./CommonButton"
import { useTimerContext } from "../contexts/TimerContext"
import LocalStorageUtil from "../utils/localStorageUtil"
import { storageKeys } from "../utils/constants"
import { useAlert } from "../contexts/AlertContext"
import { useNavigate } from "react-router-dom"
import { useProjectContext } from "../contexts/ProjectContext"
import { t } from "i18next"
import apiService from "../apiService/ApiService"
import { AxiosResponse, SaveWorkerHoursLogging, LogOutApi } from "../utils/urlConst"
import CommonLoader from "./CommonLoader"
import { styled } from "@mui/system"
import useResponsive from "../hooks/useResponsive"

interface ModalContentProps {
    maxWidth?: number | string
}
const ModalContent = styled(Box)<ModalContentProps>(({ theme, maxWidth }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    // boxShadow: theme.shadows?.[5],
    padding: theme.spacing(2, 4, 3),
    width: "100%",
    maxWidth: maxWidth,
    position: "relative",
}));

interface LogoutDialogProps {
    visibility: boolean;
    dismissModal: () => void;
}
const LogoutDialog: React.FC<LogoutDialogProps> = ({ visibility, dismissModal }) => {
    const [isVisible, setIsVisible] = useState(visibility)
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const { clearProject } = useProjectContext();
    const { setIsAuthenticated } = useTimerContext();
    const { isMobile } = useResponsive()
    const userId = LocalStorageUtil.getItem(storageKeys.userId, "");
    const flexUserId = LocalStorageUtil.getItem(storageKeys.workerId, "");
    const isFlexible = Number(LocalStorageUtil.getItem(storageKeys.isFlexRole, 0));
    const token = LocalStorageUtil.getItem(storageKeys.authToken, "");

    const messageWithTimer = "The timer is running; it will stop if you log out. Are you sure you want it to continue?"
    const {
        setIsTimerOn,
        setSelectedProjectForLog,
        setStartTime,
        isTimerOn,
        selectedProjectForLog
    } = useTimerContext();



    const handleClose = () => {
        setIsVisible(false)
        dismissModal()
    }
    const handleTimer = () => {
        if (isTimerOn) {
            setIsTimerOn(false)
            setStartTime(null)
            handleSaveEndTime()
        } else {
            handleLogout()
        }
    }
    const handleSaveEndTime = () => {
        const projectId = selectedProjectForLog?.id ?? 0
        const now = new Date()
        const endTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        saveProjectLogToServer(projectId, null, endTime)
            .finally(() => {
                setSelectedProjectForLog({
                    id: null,
                    name: null,
                })
                setIsVisible(false)
                handleLogout()
            }
            )
    }

    // fetch ProjectList api call
    const saveProjectLogToServer = async (projectId: number, startTime: string | null, endTime: string | null) => {
        try {
            setIsLoading(true)
            let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
            let medwerkerId = LocalStorageUtil.getItem(storageKeys.workerId, "");
            let isFlexRole = Number(LocalStorageUtil.getItem(storageKeys.isFlexRole, 0));

            const payload: any = {
                token: userToken,
                user_id: userId,
                medewerker_id: medwerkerId,
                project_id: projectId,
                start_time: startTime,
                end_time: endTime,
                isFlexRole: isFlexRole,
                isFromFieldApp: 1
            };

            if (endTime) {
                const d = new Date();
                payload.end_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }

            let jsondata = JSON.stringify(payload);

            apiService.setAuthToken(userToken);
            const responseJson: AxiosResponse | null = await apiService.post(SaveWorkerHoursLogging, jsondata);
            if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
                // showAlert("Log Saved Succesfully", "success")
                showAlert(startTime ? t("timer.projectStarted") : t("timer.projectLogged"), "success")
            } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
                LocalStorageUtil.clear();
                navigate("/login");
            } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
                showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
                setIsLoading(false);
            } else {
                showAlert(t("common.server_error"), 'error')
                setIsLoading(false);
            }
        } catch (error) {
            showAlert(t("common.server_error"), 'error')
        } finally {
            setIsLoading(false);
        }
    };
    const handleLogout = async () => {
        setIsLoading(true);
        try {
            const now = new Date();
            const pad = (n: number) => n.toString().padStart(2, '0');
            const actionTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
            let payload: any = {
                action_id: 2,
                action_time: actionTime,
                is_flexible: isFlexible,
                ...(isFlexible === 1 ? { flex_user_id: flexUserId } : { user_id: userId })
            };

            apiService.setAuthToken(token);
            const response: AxiosResponse | null = await apiService.post(LogOutApi, payload);
            if (response && response.status_code === 1) {
                showAlert(t("login.Logout_success"), "success");
                clearProject();
                localStorage.removeItem("isAuthenticated");
                navigate("/login");
                setIsAuthenticated(false);
                LocalStorageUtil.clear();
            } else {
                showAlert(t("common.server_error"), "error");
            }
        } catch (error) {
            showAlert(t("common.server_error"), "error");
        }
    };
    useEffect(() => {
        setIsVisible(visibility)
    }, [visibility])

    return (
        isLoading ?
            <CommonLoader isVisible={isLoading} /> :
            <StyledModal
                open={isVisible}
                onClose={handleClose}
                aria-labelledby="custom-alert-modal"
                aria-describedby="custom-alert-description"
            >
                <ModalContent maxWidth={isMobile ? 350 : isTimerOn ? 500 : 400} >
                    <CloseButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ color: "text.secondary" }}
                    >
                        <img
                            src={closeIcon}
                            alt={`close-icon`}
                            width="16px"
                            height="16px"
                        />
                    </CloseButton>

                    <Box my={2} sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <Typography sx={{
                            fontFamily: "Nunito Sans Regular500",
                            fontSize: "18px",
                            p: 1,
                            mb: 1,
                            color: theme.palette.blueColors.main
                        }}>{isTimerOn ? t("timer.logoutWithTimer") : t("timer.logout")}</Typography>

                    </Box>

                    <Box my={2} sx={{ display: "flex", minWidth: "200px", justifyContent: "center", gap: 2 }} >
                        <CommonButton
                            translationKey={"common.label_cancel"}
                            onClick={handleClose}
                            fullWidth={false}
                            sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                backgroundColor: "error.dark",
                                color: theme.palette.secondary.main,
                                px: 3
                            }}

                        />
                        <CommonButton
                            translationKey={"timer.continue"}
                            onClick={handleTimer}
                            fullWidth={false}
                            sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontWeight: 700,
                                backgroundColor: "primary.main",
                                color: theme.palette.secondary.main,
                                px: 3
                            }}

                        />
                    </Box>
                </ModalContent>
            </StyledModal>
    )
}

export default LogoutDialog

